/* ============================================================
   KOS Deployment Tracker — template loader & data injection
   ------------------------------------------------------------
   The dashboard itself is a fully self-contained HTML document
   (public/kos-tracker/template.html). This module:
     1. fetches that template once (cached),
     2. injects a parsed dataset into it (records / months / meta),
     3. yields a complete standalone HTML string suitable for both
        an <iframe srcdoc> and a downloadable .html file.
   ============================================================ */

const TEMPLATE_URL =
  (process.env.PUBLIC_URL || "") + "/kos-tracker/template.html";

let _templateCache = null;

/** Fetch the dashboard template HTML (cached after first load). */
export async function loadTemplate() {
  if (_templateCache) return _templateCache;
  const res = await fetch(TEMPLATE_URL, { cache: "no-cache" });
  if (!res.ok) {
    throw new Error(`Failed to load dashboard template (${res.status}).`);
  }
  _templateCache = await res.text();
  return _templateCache;
}

const esc = (s) =>
  String(s == null ? "" : s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/**
 * Inject a parsed dataset into the template HTML.
 * @param {string} template  raw template.html contents
 * @param {{records:Array, months:string[], periodLabel?:string, sourceLabel?:string}} data
 * @returns {string} complete standalone HTML
 */
export function injectData(template, data) {
  if (!data || !Array.isArray(data.records)) return template;

  const records = data.records;
  const months =
    data.months && data.months.length
      ? data.months
      : [...new Set(records.map((r) => r.monthKey).filter(Boolean))].sort();
  const periodLabel =
    data.periodLabel ||
    (months.length ? `${months[0]} – ${months[months.length - 1]}` : "—");
  const sourceLabel = data.sourceLabel || "Uploaded workbook";

  const recordsJson = JSON.stringify(records);
  const monthsJson = JSON.stringify(months);

  let html = template;

  // --- records array ---
  html = html.replace(
    /const KOS_RECORDS = \[[\s\S]*?\];/,
    () => "const KOS_RECORDS = " + recordsJson + ";"
  );

  // --- months axis ---
  html = html.replace(
    /const KOS_MONTHS = \[[\s\S]*?\];/,
    () => "const KOS_MONTHS = " + monthsJson + ";"
  );

  // --- META.periodLabel ---
  html = html.replace(
    /periodLabel:\s*"[^"]*"/,
    () => 'periodLabel: "' + String(periodLabel).replace(/"/g, '\\"') + '"'
  );

  // --- META.dataSource ---
  html = html.replace(
    /dataSource:\s*"[^"]*"/,
    () => 'dataSource: "' + String(sourceLabel).replace(/"/g, '\\"') + '"'
  );

  // --- data banner notice ---
  const banner =
    `<div class="databanner ok" id="dataBanner">` +
    `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>` +
    `<span><b>Live data</b>&nbsp;· imported from <b>${esc(sourceLabel)}</b> ` +
    `(${records.length} requests). Metric is <b>billed hours</b>.</span>` +
    `</div>`;
  html = html.replace(
    /<div class="databanner[^"]*" id="dataBanner">[\s\S]*?<\/div>/,
    () => banner
  );

  return html;
}

/** Build the final standalone HTML for a dataset (loads template if needed). */
export async function buildDashboardHtml(data) {
  const template = await loadTemplate();
  if (!data) return template; // default sample dashboard
  return injectData(template, data);
}

/** Trigger a browser download of an HTML string. */
export function downloadHtml(html, filename = "KOS Deployment Tracker.html") {
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}
